import { FunctionComponent } from 'react';
import { useAnimatedInteger } from '../../util/Animation';
import DigitSvg from '/assets/images/LED Digit.svg';

type Props = {
	value: number;
	digits: number;
	height?: string | number;
};

export const SegmentScore: FunctionComponent<Props> = (props) => {
	return <div style={{ width: 'fit-content', padding: '8px 8px 0 8px' }}>
		{digitValues(props).map((digitValue, digitId) => (
			<AnimatedDigit
				key={`digit-${digitId}`}
				value={digitValue}
				height={props.height}
			/>
		))}
	</div>;
};

export function digitValues(args: Props): (number | undefined)[] {
	const { value, digits } = args;
	return [...Array(digits).keys()]
		.reverse()
		.map(digitId => {
			const digitPower = Math.pow(10, digitId);
			if ((value < digitPower) && (digitId > 0)) {
				// Leading zero
				return undefined;
			} else {
				const right = value % Math.pow(10, digitId + 1);
				const float = right / digitPower;
				return Math.floor(float);
			}
		});
};

type DigitProps = {
	/** Undefined is displayed as empty - i.e. no lit segments */
	value: number | undefined;
	height?: string | number;
};

const Digit: FunctionComponent<DigitProps> = (props) => {
	const flags = ((props.value !== undefined)
		? SEGMENT_MAP[props.value]
		: undefined) ?? EMPTY_FLAGS;
	const aChar = 'a'.charCodeAt(0);

	// SVG has color attributes for each segment named '--a', '--b' etc
	const colors: Record<string, string> = {};
	for (const [id, isSet] of flags.entries()) {
		colors[`--${String.fromCharCode(aChar + id)}`] = isSet ? '#ff4a00' : '#ff4a0040';
	}

	return <DigitSvg height={props.height} style={colors} />;
};

/** 
 * Segments are a-g going clockwise from a as top segment, with g as the centre segment 
 * @see https://en.wikipedia.org/wiki/Segment_display
 */
const SEGMENT_MAP = [
	/*      a  b  c  d  e  f  g */
	/* 0 */[1, 1, 1, 1, 1, 1, 0],
	/* 1 */[0, 1, 1, 0, 0, 0, 0],
	/* 2 */[1, 1, 0, 1, 1, 0, 1],
	/* 3 */[1, 1, 1, 1, 0, 0, 1],
	/* 4 */[0, 1, 1, 0, 0, 1, 1],
	/* 5 */[1, 0, 1, 1, 0, 1, 1],
	/* 6 */[1, 0, 1, 1, 1, 1, 1],
	/* 7 */[1, 1, 1, 0, 0, 0, 0],
	/* 8 */[1, 1, 1, 1, 1, 1, 1],
	/* 9 */[1, 1, 1, 1, 0, 1, 1],
];

const EMPTY_FLAGS = [0, 0, 0, 0, 0, 0, 0, 0];

const AnimatedDigit: FunctionComponent<DigitProps> = (props) => {
	// Need to always call the hook
	const animValue = useAnimatedInteger(props.value ?? 0);
	const displayValue = (props.value === undefined) ? undefined : animValue;

	return <Digit value={displayValue} height={props.height} />;
};